// WHAT: Atomic timer component with circular progress visualization.
// WHY:  Provides countdown timer functionality with visual progress indicator for session steps, supporting start, pause, and completion callbacks.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @tracq/frontend-shared/utils, ../../utils/formatters
// RISKS:
//   - WARN: Timer uses setInterval; ensure cleanup to prevent memory leaks.
//   - NOTE: isRunning prop is deprecated; use isActive instead.
//   - WARN: Timer completion calls onComplete; ensure it doesn't cause unwanted side effects.

import React, { useEffect, useState } from "react";

import { cn } from "@/shared/utils";

import { formatDuration } from "../../utils/formatters";

export interface TimerProperties {
  className?: string;
  duration: number; // in seconds
  isActive?: boolean;
  isRunning?: boolean; // Deprecated: use isActive instead
  onComplete?: () => void;
  onStart?: () => void;
  onTick?: (remainingSeconds: number) => void;
  showProgress?: boolean;
  size?: "lg" | "md" | "sm";
}

/**
 * WHAT:
 *   Atomic timer component with circular progress visualization.
 *
 * WHY:
 *   Provides countdown timer functionality with visual progress indicator for session steps.
 *   Supports start, pause, and completion callbacks with formatted duration display.
 *
 * PROPS:
 *   - duration: number - Initial timer duration in seconds
 *   - isActive?: boolean - Flag to start/stop timer (default: false)
 *   - isRunning?: boolean - Deprecated: use isActive instead
 *   - onComplete?: () => void - Callback when timer reaches zero
 *   - onStart?: () => void - Callback when timer starts
 *   - onTick?: (remainingSeconds: number) => void - Callback on each second tick
 *   - size?: 'sm' | 'md' | 'lg' - Size variant (default: 'md')
 *   - showProgress?: boolean - Flag to show circular progress ring (default: true)
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Manages remaining time state and updates every second when active
 *   - Displays formatted duration in center of circular progress ring
 *   - Shows progress ring that fills as time decreases
 *   - Changes color to green when completed
 *   - Calls onTick callback on each second
 *   - Calls onComplete when timer reaches zero
 *   - Calls onStart when timer becomes active
 *   - Cleans up interval on unmount or when inactive
 *
 * RISKS:
 *   - WARN: Timer uses setInterval; ensure cleanup to prevent memory leaks.
 *   - NOTE: isRunning prop is deprecated; use isActive instead.
 *   - WARN: Timer completion calls onComplete; ensure it doesn't cause unwanted side effects.
 *   - NOTE: Progress calculation may show 100% before timer actually completes.
 *
 * @param root0
 *
 * @param root0.duration
 *
 * @param root0.isActive
 *
 * @param root0.isRunning
 *
 * @param root0.onComplete
 *
 * @param root0.onStart
 *
 * @param root0.onTick
 *
 * @param root0.size
 *
 * @param root0.showProgress
 *
 * @param root0.className
 *
 * @returns Timer component
 *
 */
export const Timer: React.FC<TimerProperties> = ({
  className,
  duration,
  isActive: isActiveProperty,
  isRunning, // Deprecated
  onComplete,
  onStart,
  onTick,
  showProgress = true,
  size = "md",
}) => {
  const [remainingTime, setRemainingTime] = useState(duration);
  const [isActive, setIsActive] = useState(isActiveProperty ?? isRunning ?? false);

  /**
   * WHAT:
   *   Effect hook to sync isActive state with props and call onStart callback.
   *
   * WHY:
   *   Ensures timer state matches prop changes and notifies parent when timer starts.
   *
   * SIDE EFFECTS:
   *   - Updates isActive state based on isActiveProp or deprecated isRunning prop
   *   - Calls onStart callback when timer becomes active
   *
   * RISKS:
   *   - NOTE: Dependency on onStart; ensure it's stable or memoized to prevent infinite loops.
   *   - WARN: isRunning is deprecated but still supported for backward compatibility.
   */
  useEffect(() => {
    setIsActive(isActiveProperty ?? isRunning ?? false);
    if (isActiveProperty ?? isRunning) {
      onStart?.();
    }
  }, [isActiveProperty, isRunning, onStart]);

  /**
   * WHAT:
   *   Effect hook to manage timer countdown interval.
   *
   * WHY:
   *   Handles actual timer countdown logic, updating remaining time and calling callbacks.
   *
   * SIDE EFFECTS:
   *   - Creates setInterval when timer is active and remaining time > 0
   *   - Decrements remainingTime every second
   *   - Calls onTick callback on each tick
   *   - Calls onComplete and stops timer when remainingTime reaches 0
   *   - Cleans up interval on unmount or when timer becomes inactive
   *
   * RISKS:
   *   - WARN: Interval cleanup is critical; missing cleanup causes memory leaks.
   *   - NOTE: Dependencies include onComplete and onTick; ensure they're stable or memoized.
   *   - WARN: Timer continues even if component unmounts if cleanup fails.
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isActive && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime((previous) => {
          const newTime = previous - 1;
          onTick?.(newTime);

          if (newTime <= 0) {
            setIsActive(false);
            onComplete?.();
          }

          return newTime;
        });
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isActive, remainingTime, onComplete, onTick]);

  const progress = ((duration - remainingTime) / duration) * 100;
  const isCompleted = remainingTime <= 0;

  const sizeClasses = {
    lg: "w-32 h-32 text-xl",
    md: "w-24 h-24 text-lg",
    sm: "w-16 h-16 text-sm",
  };

  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      {/* Circular Progress */}
      <div className={cn("relative", sizeClasses[size])}>
        <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
          {/* Background Circle */}
          <circle
            className="text-[#334155]"
            cx="50"
            cy="50"
            fill="none"
            r="45"
            stroke="currentColor"
            strokeWidth="4"
          />

          {/* Progress Circle */}
          {showProgress ? (
            <circle
              className={cn(
                "transition-all duration-1000 ease-linear",
                isCompleted ? "text-green-400" : "text-tracq-bright-blue",
              )}
              cx="50"
              cy="50"
              fill="none"
              r="45"
              stroke="currentColor"
              strokeDasharray={String(2 * Math.PI * 45)}
              strokeDashoffset={String(2 * Math.PI * 45 * (1 - progress / 100))}
              strokeLinecap="round"
              strokeWidth="4"
            />
          ) : null}
        </svg>

        {/* Timer Text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn("font-mono font-bold", isCompleted ? "text-green-400" : "text-[#F1F5F9]")}
          >
            {formatDuration(remainingTime)}
          </span>
        </div>
      </div>
    </div>
  );
};
